import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useRequestScope } from '@/modules/service-requests/shared/request-scope';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-requests/shared/components/applications-table';
import { buildMetlifeFilter, EMPTY_METLIFE_FILTERS } from '../../utils/metlife-filter';
import { useChangeMetlifeStatus, useMetlifeApplications } from '../../hooks/use-metlife';
import { MetlifeApplication, MetlifeStatus } from '../../types/metlife.types';
import { createMetlifeRequestColumns } from '../metlife-columns/metlife-columns';
import { MetlifeCompleteDialog } from '../metlife-complete-dialog/metlife-complete-dialog';
import { MetlifeDetailsDialog } from '../metlife-details-dialog/metlife-details-dialog';
import { appPath, SERVICE_REQUEST_TYPES } from '@/constant/app-paths';

export const MetlifeRequests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canUpdateStatus = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.INSURANCE_UPDATE_STATUS);
  const canExclude = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.INSURANCE_EXCLUDE);
  const table = useApplicationsTable();
  const [detailsTarget, setDetailsTarget] = useState<MetlifeApplication | null>(null);
  const [completeTarget, setCompleteTarget] = useState<MetlifeApplication | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{
    application: MetlifeApplication;
    status: 'Archived' | 'Excluded';
  } | null>(null);

  // Every filter is a column filter now; this keeps only the reviewer's department scope.
  const { departments, getDepartmentIdCondition, isLoading: isScopeLoading } = useRequestScope();

  const { data, isLoading, error } = useMetlifeApplications({
    pageNo: table.pagination.pageIndex + 1,
    pageSize: table.pagination.pageSize,
    filter: mergeWhere(
      buildMetlifeFilter(EMPTY_METLIFE_FILTERS, getDepartmentIdCondition('')),
      table.columnWhere
    ),
    order: table.order,
  });
  const {
    mutateAsync: changeStatus,
    isPending,
    variables: savingStatus,
  } = useChangeMetlifeStatus();
  const savingId = isPending ? savingStatus?.itemId : undefined;

  const applyStatus = useCallback(
    (application: MetlifeApplication, status: MetlifeStatus, metLifeIdNumber?: string) =>
      changeStatus({ itemId: application.ItemId, status, metLifeIdNumber }),
    [changeStatus]
  );

  const columns = useMemo(
    () =>
      createMetlifeRequestColumns({
        t,
        departments,
        canUpdateStatus,
        canExclude,
        savingId,
        onViewDetails: setDetailsTarget,
        onEdit: (application) =>
          navigate(appPath.serviceRequestForm(SERVICE_REQUEST_TYPES.INSURANCE, application.ItemId)),
        // Completing needs the insurer's policy number first; every other move applies directly.
        onChangeStatus: (application, status) =>
          status === 'Completed'
            ? setCompleteTarget(application)
            : applyStatus(application, status),
        onArchive: (application) => setConfirmTarget({ application, status: 'Archived' }),
        onExclude: (application) => setConfirmTarget({ application, status: 'Excluded' }),
      }),
    [t, departments, canUpdateStatus, canExclude, savingId, navigate, applyStatus]
  );

  const isExclude = confirmTarget?.status === 'Excluded';

  return (
    <div className="flex flex-col gap-4">
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading || isScopeLoading}
        error={error}
        table={table}
      />

      <MetlifeDetailsDialog
        application={detailsTarget}
        onOpenChange={() => setDetailsTarget(null)}
      />
      <MetlifeCompleteDialog
        open={Boolean(completeTarget)}
        onOpenChange={(open) => !open && setCompleteTarget(null)}
        initialMetLifeId={completeTarget?.MetLifeIdNumber}
        isPending={isPending}
        onConfirm={async (metLifeIdNumber) => {
          if (!completeTarget) return;
          try {
            await applyStatus(completeTarget, 'Completed', metLifeIdNumber);
            setCompleteTarget(null);
          } catch {
            // Error toast is already shown by the mutation's global error handler.
          }
        }}
      />
      <ConfirmationModal
        open={Boolean(confirmTarget)}
        onOpenChange={(open) => !open && setConfirmTarget(null)}
        title={t(isExclude ? 'EXCLUDE_METLIFE' : 'ARCHIVE_METLIFE')}
        description={t(
          isExclude ? 'ARE_YOU_SURE_TO_EXCLUDE_METLIFE' : 'ARE_YOU_SURE_TO_ARCHIVE_METLIFE'
        )}
        confirmText={t(isExclude ? 'EXCLUDE' : 'ARCHIVE')}
        onConfirm={() =>
          confirmTarget && applyStatus(confirmTarget.application, confirmTarget.status)
        }
      />
    </div>
  );
};
